import { useState, useCallback, useEffect } from 'react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { ChevronLeft, ChevronRight, Building2, CheckCircle2, Save, Loader2, CreditCard } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SUBMIT_DEAL_TRANSLATIONS } from '@/constants/submit-deal.translations';
import { SubmitDealFormData, UploadedFiles, createInitialFormData } from '@/types/submit-deal.types';
import { handleSubmitDeal } from '@/services/cim-generation.service';
import { performFinancialAnalysis } from '@/utils/financial-analysis';
import { validateStep } from '@/schemas/dealSubmissionSchema';
import { redirectToSellerCheckout } from '@/lib/seller-checkout';
import {
  StepGeneral, StepOperations, StepLicenses, StepAssets, StepRealEstate,
  StepFinancials, StepAddbacks, StepProjections, StepCulture, StepSwot,
  StepTOVScore, StepDocuments
} from '@/components/submit-deal';

const TOTAL_STEPS = 12;

const SubmitDeal = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const t = SUBMIT_DEAL_TRANSLATIONS[language as 'en' | 'fr'] || SUBMIT_DEAL_TRANSLATIONS.en;

  const [formData, setFormData] = useState<SubmitDealFormData>(createInitialFormData());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [paymentCompleted, setPaymentCompleted] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFiles>({
    documents: [], photos: [], logo: null
  });

  // Capture sourcer referral (?ref=&role=) 
  useEffect(() => {
    const ref = searchParams.get('ref');
    const role = searchParams.get('role');
    if (ref) {
      try {
        localStorage.setItem(
          'dealSourcerReferral',
          JSON.stringify({ 
            sourcer_uid: ref, 
            sourcer_role: role || 'deal_sourcer', 
            capturedAt: Date.now() 
          })
        );
      } catch {}
    }
  }, [searchParams]);

  // Check for payment success from URL params
  useEffect(() => {
    const payment = searchParams.get('payment');
    if (payment === 'success') {
      setPaymentCompleted(true);
      toast({
        title: language === 'fr' ? 'Paiement réussi!' : 'Payment successful!',
        description: language === 'fr' 
          ? 'Vous pouvez maintenant soumettre votre deal.' 
          : 'You can now submit your deal.',
      });
      // Clean URL params
      setSearchParams({});
    } else if (payment === 'canceled') {
      toast({
        title: language === 'fr' ? 'Paiement annulé' : 'Payment canceled',
        description: language === 'fr' 
          ? 'Vous pouvez réessayer quand vous êtes prêt.' 
          : 'You can try again when ready.',
        variant: 'destructive',
      });
      setSearchParams({});
    }
  }, [searchParams, setSearchParams, toast, language]);

  const updateFormData = useCallback(<K extends keyof SubmitDealFormData>(
    section: K, updates: Partial<SubmitDealFormData[K]>
  ) => {
    setFormData(prev => ({
      ...prev,
      [section]: { ...prev[section] as object, ...updates }
    }));
  }, []);

  const clearError = useCallback((field: string) => {
    setErrors(prev => { const n = { ...prev }; delete n[field]; return n; });
  }, []);

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};
    const currentYear = new Date().getFullYear();

    if (step === 1) {
      if (!formData.general.legalName || formData.general.legalName.length < 2) newErrors.legalName = t.validation.legalNameMinLength;
      if (!formData.general.entityType) newErrors.entityType = t.validation.entityTypeRequired;
      if (!formData.general.title || formData.general.title.length < 3) newErrors.title = t.validation.titleMinLength;
      if (!formData.general.description || formData.general.description.length < 50) newErrors.description = t.validation.descriptionMinLength;
      if (!formData.general.industry) newErrors.industry = t.validation.industryRequired;
      if (!formData.general.locationCity) newErrors.locationCity = t.validation.cityRequired;
      if (!formData.general.locationCountry) newErrors.locationCountry = t.validation.countryRequired;
      if (currentYear - formData.general.yearEstablished < 3) newErrors.yearEstablished = t.validation.businessTooYoung;
      if (formData.general.brokerId && !formData.general.representedSellerId) {
        newErrors.representedSellerId = language === 'fr'
          ? 'Veuillez sélectionner un vendeur représenté avant de continuer.'
          : 'Please select a represented seller before continuing.';
      }
    } else if (step === 2) {
      if (formData.operations.ownerHoursPerWeek < 0 || formData.operations.ownerHoursPerWeek > 168) newErrors.ownerHoursPerWeek = t.validation.ownerHoursInvalid;
    } else if (step === 6) {
      if (formData.financials.askingPrice < 1000) newErrors.askingPrice = t.validation.askingPriceMinimum;
      if (formData.financials.cashFlowEquity <= 0) newErrors.cashFlowEquity = t.validation.cashFlowPositive;
    } else if (step === 10) {
      if (formData.swot.strengths.length < 10) newErrors.strengths = t.validation.swotMinLength.replace('{field}', t.step10.strengths);
      if (formData.swot.weaknesses.length < 10) newErrors.weaknesses = t.validation.swotMinLength.replace('{field}', t.step10.weaknesses);
      if (formData.swot.opportunities.length < 10) newErrors.opportunities = t.validation.swotMinLength.replace('{field}', t.step10.opportunities);
      if (formData.swot.threats.length < 10) newErrors.threats = t.validation.swotMinLength.replace('{field}', t.step10.threats);
    } else if (step === 11) {
      // TOV Score — no required fields, all optional
      // Score auto-calculated, no validation needed
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(formData.currentStep)) {
      setFormData(prev => ({ ...prev, currentStep: Math.min(prev.currentStep + 1, TOTAL_STEPS) }));
      window.scrollTo(0, 0);
    }
  };

  const prevStep = () => {
    setFormData(prev => ({ ...prev, currentStep: Math.max(prev.currentStep - 1, 1) }));
    window.scrollTo(0, 0);
  };

  const handleFileUpload = (type: 'documents' | 'photos' | 'logo', files: FileList) => {
    const fileArray = Array.from(files).map(f => ({ name: f.name, size: f.size, type: f.type, url: URL.createObjectURL(f) }));
    setUploadedFiles(prev => type === 'logo' ? { ...prev, logo: fileArray[0] || null } : { ...prev, [type]: [...prev[type], ...fileArray] });
  };

  const handleFileRemove = (type: 'documents' | 'photos' | 'logo', index?: number) => {
    setUploadedFiles(prev => type === 'logo' ? { ...prev, logo: null } : { ...prev, [type]: prev[type].filter((_, i) => i !== index) });
  };

  const saveDraft = () => {
    localStorage.setItem('dealSubmissionDraft', JSON.stringify({ formData, uploadedFiles }));
    toast({ title: t.messages.draftSaved, description: t.messages.draftSavedDescription });
  };

  // Handle seller pre-evaluation payment checkout
  const handleSellerPayment = async () => {
    if (!user) {
      toast({ title: t.messages.authRequired, variant: 'destructive' });
      return;
    }

    try {
      setPaymentProcessing(true);

      localStorage.setItem('dealSubmissionPending', JSON.stringify({ formData, uploadedFiles }));

      await redirectToSellerCheckout({
        paymentStep: 'pre_evaluation',
        userType: 'seller',
        dealId: 'pending',
        email: user.email || '',
      });
    } catch (error: any) {
      console.error('Seller checkout error:', error);
      toast({
        title: language === 'fr' ? 'Erreur' : 'Error',
        description: error.message || (language === 'fr' 
          ? 'Une erreur est survenue lors de la création du paiement.' 
          : 'An error occurred while creating the payment.'),
        variant: 'destructive',
      });
    } finally {
      setPaymentProcessing(false);
    }
  };

  const handleSubmit = async () => {
    if (!user) { toast({ title: t.messages.authRequired, variant: 'destructive' }); return; }
    if (!validateStep(formData.currentStep)) return;

    // If payment not completed, trigger payment first
    if (!paymentCompleted) {
      handleSellerPayment();
      return;
    }

    try {
      setSubmitting(true);

      // Use the new CIM generation service which handles:
      // 1. Saving to Firestore with 'awaiting_review' status
      // 2. Automatic financial analysis (EBITDA, Multiples)
      // 3. AI-generated CIM narrative
      // 4. CIM entry creation for staff review
      const { dealId, financialAnalysis } = await handleSubmitDeal(
        formData,
        user.id,
        user.email || ''
      );

      console.log('Deal submitted with ID:', dealId);
      console.log('Financial Analysis:', financialAnalysis);

      toast({ title: t.messages.submitSuccess, description: t.messages.submitSuccessDescription });
      localStorage.removeItem('dealSubmissionDraft');
      setPaymentCompleted(false); // Reset for next submission
      setTimeout(() => navigate('/submissions'), 2000);
    } catch (error) {
      console.error('Error submitting deal:', error);
      toast({ title: t.messages.submitError, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const stepProps = { formData, updateFormData, errors, clearError, t };
  const stepTitles = Object.values(t.steps);

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-4xl mx-auto">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">{t.pageTitle}</h1>
            <p className="text-muted-foreground mt-1">{t.pageSubtitle}</p>
          </div>
          <Building2 className="h-8 w-8 text-primary" />
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>{stepTitles[formData.currentStep - 1]}</CardTitle>
                <CardDescription>{t.stepOf.replace('{current}', formData.currentStep.toString()).replace('{total}', TOTAL_STEPS.toString())}</CardDescription>
              </div>
              <Progress value={(formData.currentStep / TOTAL_STEPS) * 100} className="w-32" />
            </div>
          </CardHeader>
          <CardContent>
            {/* Step Indicator */}
            <div className="flex items-center justify-between mb-8 overflow-x-auto pb-2">
              {stepTitles.map((title, index) => (
                <div key={index} className="flex items-center flex-shrink-0">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold ${formData.currentStep > index + 1 ? 'bg-primary text-primary-foreground' : formData.currentStep === index + 1 ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
                    {formData.currentStep > index + 1 ? <CheckCircle2 className="h-4 w-4" /> : index + 1}
                  </div>
                  {index < stepTitles.length - 1 && <div className={`h-0.5 w-4 mx-1 ${formData.currentStep > index + 1 ? 'bg-primary' : 'bg-muted'}`} />}
                </div>
              ))}
            </div>

            {/* Step Content */}
            {formData.currentStep === 1 && <StepGeneral {...stepProps} />}
            {formData.currentStep === 2 && <StepOperations {...stepProps} />}
            {formData.currentStep === 3 && <StepLicenses {...stepProps} />}
            {formData.currentStep === 4 && <StepAssets {...stepProps} />}
            {formData.currentStep === 5 && <StepRealEstate {...stepProps} />}
            {formData.currentStep === 6 && <StepFinancials {...stepProps} />}
            {formData.currentStep === 7 && <StepAddbacks {...stepProps} />}
            {formData.currentStep === 8 && <StepProjections {...stepProps} />}
            {formData.currentStep === 9 && <StepCulture {...stepProps} />}
            {formData.currentStep === 10 && <StepSwot {...stepProps} />}
            {formData.currentStep === 11 && <StepTOVScore {...stepProps} />}
            {formData.currentStep === 12 && <StepDocuments {...stepProps} uploadedFiles={uploadedFiles} onFileUpload={handleFileUpload} onFileRemove={handleFileRemove} />}

            {/* Navigation */}
            <div className="flex justify-between mt-8 pt-6 border-t">
              <Button variant="outline" onClick={prevStep} disabled={formData.currentStep === 1}>
                <ChevronLeft className="h-4 w-4 mr-2" /> {t.previous}
              </Button>
              <Button variant="secondary" onClick={saveDraft}>
                <Save className="h-4 w-4 mr-2" /> {t.saveDraft}
              </Button>
              {formData.currentStep < TOTAL_STEPS ? (
                <Button onClick={nextStep}>{t.next} <ChevronRight className="h-4 w-4 ml-2" /></Button>
              ) : (
                <Button 
                  onClick={handleSubmit} 
                  disabled={submitting || paymentProcessing}
                  className={paymentCompleted ? 'bg-green-600 hover:bg-green-700' : ''}
                >
                  {paymentProcessing ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {language === 'fr' ? 'Traitement...' : 'Processing...'}
                    </>
                  ) : submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {language === 'fr' ? 'Soumission...' : 'Submitting...'}
                    </>
                  ) : paymentCompleted ? (
                    <>
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      {t.submit}
                    </>
                  ) : (
                    <>
                      <CreditCard className="h-4 w-4 mr-2" />
                      {language === 'fr' ? 'Payer et Soumettre' : 'Pay & Submit'}
                    </>
                  )}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default SubmitDeal;
